export interface STEmbedConfig {
  id?: string;
  url: string; // Full URL of embedded app
  mountSelector: string; // e.g., '#embed-container'
  iframeAttributes?: Partial<HTMLIFrameElement>;
}

export enum FrameMessageType {
  READY = 'READY',
  ERROR = 'ERROR',
  UNAUTHORIZED = 'UNAUTHORIZED',
  MESSAGE = 'MESSAGE',
  FILTERS = 'FILTERS',
  UPDATE_FILTERS = 'UPDATE_FILTERS',
  CHECK_READY = 'CHECK_READY',
}

export interface MessagePayload {
  token: string;
  selectedLocationIds: number[];
  selectedRegionsIds: number[];
  userData: any;
  parentFullUrl: string;
}

export interface FrameMessage<T> {
  type: FrameMessageType;
  data?: T; // Data to be sent to the iframe and accepted by the iframe
}

type MessageCallback = (msg: any) => void;

export class STEmbedFrame<T> {
  private iframe!: HTMLIFrameElement;
  private readonly targetOrigin: string = '';
  private isIframeReady = false;
  private readonly messageQueue: any[] = [];
  private readonly messageListeners: MessageCallback[] = [];
  private checkReadyInterval: any = null;

  constructor(private readonly config: STEmbedConfig) {
    this.targetOrigin = new URL(config.url).origin;
    this.initIframe();
    this.listenForReady();
    this.listenForMessagesInternal();
  }

  private initIframe() {
    this.iframe = document.createElement('iframe');
    this.iframe.src = this.config.url;
    this.iframe.style.border = '0';
    this.iframe.style.width = '100%';
    this.iframe.style.height = '100%';
    this.iframe.allow = 'fullscreen';

    if (this.config.iframeAttributes) {
      Object.entries(this.config.iframeAttributes).forEach(([key, value]) => {
        (this.iframe as any)[key] = value;
      });
    }
    const mountPoint = document.getElementById(this.config.mountSelector);
    if (!mountPoint)
      throw new Error(`Mount selector ${this.config.mountSelector} not found`);
    mountPoint.innerHTML = '';
    mountPoint.appendChild(this.iframe);

    this.checkReadyInterval = setInterval(() => {
      if (this.iframe.contentWindow) {
        this.iframe.contentWindow.postMessage(
          { type: FrameMessageType.CHECK_READY },
          this.targetOrigin,
        );
      }
    }, 1000);
  }

  private listenForReady() {
    window.addEventListener('message', event => {
      if (event.origin !== this.targetOrigin || !event.data) return;
      if (event.data.type === FrameMessageType.READY) {
        if (this.checkReadyInterval) {
          clearInterval(this.checkReadyInterval);
          this.checkReadyInterval = null;
        }
        this.isIframeReady = true;
        this.flushQueue();
      }
    });
  }

  private listenForMessagesInternal() {
    window.addEventListener('message', event => {
      if (event.origin !== this.targetOrigin || !event.data) return;
      for (const cb of this.messageListeners) {
        cb(event.data);
      }
    });
  }

  sendMessage(message: FrameMessage<Partial<T>>) {
    if (this.isIframeReady) {
      this.iframe.contentWindow?.postMessage(message, this.targetOrigin);
    } else {
      this.messageQueue.push(message);
    }
  }

  onMessage(callback: MessageCallback) {
    this.messageListeners.push(callback);
  }

  private flushQueue() {
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      this.sendMessage(message);
    }
  }
}
